import Typography from "@mui/joy/Typography";
import { useTranslation } from "../../../i18n";
import { useTeam } from "../TeamLayout";
import {
  Sku,
  useSkuListQuery,
  useSkuUpsertMutation,
} from "../../../__generated__/types-and-hooks";
import {
  AspectRatio,
  Breadcrumbs,
  Button,
  Box,
  Link,
  Checkbox,
  Input,
} from "@mui/joy";
import { Link as RouterLink } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import AddIcon from "@mui/icons-material/Add";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  // GridRenderCellParams,
  GridRowId,
  GridRowModel,
} from "@mui/x-data-grid";
// import TuneIcon from "@mui/icons-material/TuneRounded";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import BookInspectionModal from "./BookInspectionModal";
import { useNotify } from "../../../hooks/useNotify";
// import SkuRef from "../../../components/SkuRef";
import Fuse from "fuse.js";
import SearchIcon from "@mui/icons-material/Search";
import SkuAddModal from "./SkuAddModal";
import SkuImportModal from "./SkuImportModal";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SkuRef from "../../../components/SkuRef";
import GridStyles from "../../../components/datagrid/GridStyles";

const JoyCheckbox = React.forwardRef(function JoyCheckbox(props: any, ref) {
  const { indeterminate, checked, ...other } = props;
  return (
    <Checkbox
      ref={ref}
      indeterminate={indeterminate}
      checked={checked}
      variant="outlined"
      color="neutral"
      {...other}
    />
  );
});

export default function Skus() {
  const notify = useNotify();
  const t = useTranslation();
  const teamCtx = useTeam();
  const [search, setSearch] = useState<string | null>(null);
  const [skusFiltered, setSkusFiltered] = useState<Sku[] | undefined>(
    undefined
  );

  const RefRenderCell = useCallback(
    (params: GridRenderCellParams<any, string>) => {
      return (
        <SkuRef uniqueId={params.row.uniqueId} teamSlug={teamCtx.team.slug} />
      );
    },
    [teamCtx]
  );

  const [bookInspectionData, setBookInspectionData] = useState<null | Sku[]>(
    null
  );
  const [skuModalAdd, setSkuAddModal] = useState<boolean>(false);
  const [skuImportModal, setSkuImportModal] = useState<boolean>(false);

  const skuListQuery = useSkuListQuery({
    variables: { teamId: teamCtx.team.id },
  });
  const [skuUpsertMutation] = useSkuUpsertMutation({});

  const skus = skuListQuery.data && skuListQuery.data.sku_list?.skus;

  const fuse = useMemo(() => {
    if (!skus) return null;

    return new Fuse(skus, {
      keys: [
        { name: "uniqueId", weight: 0.8 },
        { name: "sku", weight: 0.5 },
        { name: "name", weight: 0.3 },
        { name: "description", weight: 0.2 },
        { name: "category", weight: 0.1 },
        { name: "material", weight: 0.1 },
        { name: "asin", weight: 0.1 },
        { name: "hs", weight: 0.1 },
        { name: "hts", weight: 0.1 },
        { name: "upc", weight: 0.1 },
      ],
      threshold: 0.2, // 0 is exact, 1 is very fuzzy
      includeScore: true,
    });
  }, [skus]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSkusFiltered(
        fuse && search && search.trim() !== ""
          ? fuse.search(search.trim()).map((result) => result.item)
          : skus
      );
    }, 500);

    return () => clearTimeout(handler); // cleanup on every keystroke
  }, [search, skus, fuse]);

  const [selectionModel, setSelectionModel] = React.useState<number[]>([]);
  const handleBookInspection = useCallback(() => {
    const selectedIds = new Set(selectionModel);
    const selectedSkus = (skus ?? []).filter((sku) => selectedIds.has(sku.id));

    setBookInspectionData(selectedSkus);
  }, [selectionModel, skus]);

  const handleAddSku = useCallback(() => {
    setSkuAddModal(true);
  }, [setSkuAddModal]);

  const handleImportModal = useCallback(() => {
    setSkuImportModal(true);
  }, [setSkuImportModal]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "uniqueId",
        headerName: "ID",
        width: 80,
        editable: false,
        renderCell: RefRenderCell,
      },
      {
        field: "sku",
        headerName: "SKU",
        width: 200,
        editable: false,
      },
      {
        field: "name",
        headerName: "Name",
        width: 200,
        editable: false,
      },
      {
        field: "description",
        headerName: "Description",
        width: 180,
        editable: false,
      },
      {
        field: "category",
        headerName: "Category",
        width: 180,
        editable: false,
      },
      {
        field: "material",
        headerName: "Material",
        width: 80,
        editable: false,
      },
      {
        field: "asin",
        headerName: "ASIN",
        width: 80,
        editable: false,
      },
      {
        field: "hs",
        headerName: "HS",
        width: 80,
        editable: false,
      },
      {
        field: "hts",
        headerName: "HTS",
        width: 80,
        editable: false,
      },
      {
        field: "upc",
        headerName: "UPC",
        width: 80,
        editable: false,
      },
    ],
    [RefRenderCell]
  );

  const handleProcessRowUpdate = useCallback(
    async (
      newRow: GridRowModel,
      oldRow: GridRowModel,
      { rowId }: { rowId: GridRowId }
    ) => {
      const delta = (field: string, key?: string) =>
        newRow[field] !== oldRow[field]
          ? typeof key === "string" && typeof newRow[field] === "object"
            ? newRow[field][key]
            : newRow[field]
          : void 0;

      await skuUpsertMutation({
        variables: {
          updates: {
            id: rowId as number,
            teamId: teamCtx.team.id!,
            sku: delta("sku"),
            name: delta("name"),
            description: delta("description"),
            category: delta("category"),
            material: delta("material"),
            asin: delta("asin"),
            hs: delta("hs"),
            hts: delta("hts"),
            upc: delta("upc"),
          },
        },
        onError(error, clientOptions) {
          notify({ type: "danger", msg: error.message });
        },
      });

      return newRow;
    },
    [skuUpsertMutation, teamCtx, notify]
  );

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumbs"
        sx={{
          "--Breadcrumbs-gap": "4px",
          m: 1,
        }}
        size="sm"
      >
        <Link component={RouterLink} color="neutral" to="/">
          <HomeIcon />
        </Link>
        <Link
          component={RouterLink}
          color="neutral"
          to={`/${teamCtx.team.slug}`}
        >
          {teamCtx.team.name}
        </Link>
        <Typography level="title-sm">{t("SKU")}</Typography>
      </Breadcrumbs>
      <Box sx={{ display: "flex", m: 2 }} gap={1}>
        <Input
          type="text"
          placeholder={t("Search...")}
          startDecorator={<SearchIcon />}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Box sx={{ flex: 1 }}></Box>
        {selectionModel.length > 0 && (
          <Button
            variant="solid"
            color="primary"
            startDecorator={<FactCheckIcon />}
            onClick={handleBookInspection}
          >
            {t("Inspect")}
          </Button>
        )}

        {/* <Button
          variant="outlined"
          color="neutral"
          startDecorator={<TuneIcon />}
          // onClick={() => setOpen(true)}
        >
          {t("Filters")}
        </Button> */}
        <Button
          // loading={loading}

          startDecorator={<AddIcon />}
          variant="outlined"
          onClick={handleAddSku}
        >
          {t("Add")}
        </Button>
        <Button
          // loading={loading}

          startDecorator={<UploadFileIcon />}
          variant="outlined"
          onClick={handleImportModal}
        >
          {t("Import")}
        </Button>
      </Box>
      <Box
        sx={{
          gap: 2,
          px: 2,
          display: "flex",
          flexDirection: "column",
          height: "calc(100% - 120px)",
        }}
      >
        <DataGrid
          {...GridStyles}
          checkboxSelection
          loading={skuListQuery.loading}
          rows={skusFiltered ?? skus ?? []}
          columns={columns}
          editMode="cell"
          processRowUpdate={handleProcessRowUpdate}
          slots={{
            baseCheckbox: JoyCheckbox,
          }}
          onRowSelectionModelChange={(newSelection) => {
            setSelectionModel(newSelection as number[]);
          }}
        />
        {skus?.length === 0 && (
          <>
            <Box
              sx={{
                gap: 2,
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography color="neutral" level="body-md">
                {t(
                  "Manage all your SKUs in one place to streamline your supply chain operations. Add SKUs manually or connect with integrations like Anvyl or NetSuite."
                )}
              </Typography>
              <RouterLink to={`/${teamCtx.team.slug}/settings/integrations`}>
                {t("Settings/Integrations")}
              </RouterLink>
              <AspectRatio
                variant="plain"
                ratio={1}
                objectFit="contain"
                maxHeight={"50vh"}
                sx={{ bgcolor: "transparent" }}
              >
                <img
                  src={"/images/skus_empty_state.png"}
                  alt={t("No SKUs yet")}
                />
              </AspectRatio>
            </Box>
            <Box
              sx={{
                textAlign: "center",
              }}
            >
              <Button sx={{ mb: 2 }} variant="outlined">
                {t("Create New SKU")}
              </Button>
            </Box>
          </>
        )}
      </Box>
      <BookInspectionModal
        open={bookInspectionData !== null}
        skus={bookInspectionData ?? []}
        onClose={() => setBookInspectionData(null)}
      />
      <SkuAddModal open={skuModalAdd} onClose={() => setSkuAddModal(false)} />
      <SkuImportModal
        open={skuImportModal}
        onClose={() => setSkuImportModal(false)}
      />
    </>
  );
}
